import React from 'react';
import {ActivityIndicator} from 'react-native';
import {useNavigation} from '@react-navigation/native';

import {Button} from '../../../../components/Interface/Forms/Buttons/Button';
import {formatDateBR, formatCentavos} from '../../../../utils/format';
import {
  listarParcelasEmprestimo,
  ListarParcelasResponse,
  ParcelaStatusVisual,
  EmprestimoItemAPI,
} from '../../../../services/creditoMova';

import {
  Container,
  Sheet,
  Handle,
  SheetHeader,
  SheetTitle,
  SheetSubtitle,
  ScrollContent,
  ParcelaCard,
  ParcelaRow,
  ParcelaLeft,
  ParcelaNumber,
  ParcelaDate,
  ParcelaRight,
  ParcelaValue,
  StatusPill,
  StatusText,
  EncargosBreakdown,
  EncargosRow,
  EncargosLabel,
  EncargosValue,
  EncargosDivider,
  ButtonWrapper,
  LoadingWrapper,
  UnavailableContainer,
  UnavailableIcon,
  UnavailableTitle,
  UnavailableText,
} from './styles';

type Props = {
  visible: boolean;
  onDismiss: () => void;
  emprestimo: EmprestimoItemAPI | null;
};

function getStatusLabel(status: ParcelaStatusVisual): string {
  switch (status) {
    case 'a_vencer':
      return 'A vencer';
    case 'vence_hoje':
      return 'Vence hoje';
    case 'em_atraso':
      return 'Em atraso';
    case 'paga':
      return 'Paga';
  }
}

export function ParcelasBottomSheet({visible, onDismiss, emprestimo}: Props) {
  const navigation = useNavigation();
  const [loading, setLoading] = React.useState(false);
  const [data, setData] = React.useState<ListarParcelasResponse | null>(null);

  React.useEffect(() => {
    if (visible && emprestimo) {
      setData(null);
      setLoading(true);
      listarParcelasEmprestimo(emprestimo.operacao_id)
        .then(res => setData(res))
        .catch(() => setData(null))
        .finally(() => setLoading(false));
    }
  }, [visible, emprestimo]);

  const contratadoEm = emprestimo?.data_assinatura || emprestimo?.data_aprovacao;

  return (
    <Container
      isVisible={visible}
      onBackButtonPress={onDismiss}
      onBackdropPress={onDismiss}
      propagateSwipe>
      <Sheet>
        <Handle />

        <SheetHeader>
          <SheetTitle>Parcelas</SheetTitle>
          {contratadoEm && (
            <SheetSubtitle>Contratado em {formatDateBR(contratadoEm)}</SheetSubtitle>
          )}
        </SheetHeader>

        {loading && (
          <LoadingWrapper>
            <ActivityIndicator size="large" />
          </LoadingWrapper>
        )}

        {!loading && (!data || !data.parcelas_disponiveis) && (
          <UnavailableContainer>
            <UnavailableIcon name="clock" />
            <UnavailableTitle>Parcelas não disponíveis</UnavailableTitle>
            <UnavailableText>
              As parcelas ainda não estão disponíveis.{'\n'}
              Tente novamente mais tarde.
            </UnavailableText>
          </UnavailableContainer>
        )}

        {!loading && data && data.parcelas_disponiveis && (
          <ScrollContent
            showsVerticalScrollIndicator={false}
            nestedScrollEnabled
            contentContainerStyle={{paddingBottom: 24}}>
            {data.items.map(parcela => (
              <ParcelaCard key={parcela.id} status={parcela.status_visual}>
                <ParcelaRow>
                  <ParcelaLeft>
                    <ParcelaNumber>
                      Parcela {parcela.numero_parcela} de {data.total_parcelas}
                    </ParcelaNumber>
                    <ParcelaDate>
                      Vencimento: {formatDateBR(parcela.data_vencimento)}
                    </ParcelaDate>
                  </ParcelaLeft>
                  <ParcelaRight>
                    <ParcelaValue>
                      {formatCentavos(parcela.valor_atualizado_centavos)}
                    </ParcelaValue>
                    <StatusPill status={parcela.status_visual}>
                      <StatusText>{getStatusLabel(parcela.status_visual)}</StatusText>
                    </StatusPill>
                  </ParcelaRight>
                </ParcelaRow>

                {parcela.status_visual === 'em_atraso' &&
                  parcela.encargos_atraso_centavos > 0 && (
                    <EncargosBreakdown>
                      <EncargosRow>
                        <EncargosLabel>Valor original</EncargosLabel>
                        <EncargosValue style={{color: undefined}}>
                          {formatCentavos(parcela.valor_parcela_centavos)}
                        </EncargosValue>
                      </EncargosRow>
                      {parcela.multa_atraso_centavos > 0 && (
                        <EncargosRow>
                          <EncargosLabel>Multa</EncargosLabel>
                          <EncargosValue>
                            + {formatCentavos(parcela.multa_atraso_centavos)}
                          </EncargosValue>
                        </EncargosRow>
                      )}
                      {parcela.mora_atraso_centavos > 0 && (
                        <EncargosRow>
                          <EncargosLabel>Mora</EncargosLabel>
                          <EncargosValue>
                            + {formatCentavos(parcela.mora_atraso_centavos)}
                          </EncargosValue>
                        </EncargosRow>
                      )}
                      <EncargosDivider />
                      <EncargosRow>
                        <EncargosLabel>Total atualizado</EncargosLabel>
                        <EncargosValue>
                          {formatCentavos(parcela.valor_atualizado_centavos)}
                        </EncargosValue>
                      </EncargosRow>
                    </EncargosBreakdown>
                  )}

                {parcela.pode_pagar && (
                  <ButtonWrapper>
                    <Button
                      title="Pagar parcela"
                      enabled
                      onPress={() => {
                        onDismiss();
                        navigation.navigate('EmprestimoPagamento', {
                          operacao_mova_id: emprestimo!.operacao_id,
                          parcela_mova_id: parcela.id,
                          tipo_operacao: 'parcela_mova',
                          numero_parcela: parcela.numero_parcela,
                          data_vencimento: parcela.data_vencimento,
                          valor_centavos: parcela.valor_atualizado_centavos,
                        });
                      }}
                    />
                  </ButtonWrapper>
                )}
              </ParcelaCard>
            ))}
          </ScrollContent>
        )}
      </Sheet>
    </Container>
  );
}
